import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Package } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api, formatMXN } from '@/lib/api';
import {
  claseBadgeEnvio,
  claseBadgePago,
  etiquetaEstatusEnvio,
  etiquetaEstatusPago,
  etiquetaMetodoPago,
  formatearFechaPedido,
} from '@/lib/pedidos';
import type { PedidoCliente, PedidoDetalle } from '@/types';
import { CatalogBanner } from '@/components/layout/CatalogBanner';
import { Panel } from '@/components/ui';

export function LoginPage() {
  const [params] = useSearchParams();
  const redirect = params.get('redirect') ?? '/cuenta';
  const [usuario, setUsuario] = useState(() => params.get('correo') ?? params.get('usuario') ?? '');
  const [clave, setClave] = useState('');
  const [error, setError] = useState<string | null>(null);
  const login = useAuthStore((s) => s.login);
  const loading = useAuthStore((s) => s.loading);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login(usuario.trim(), clave);
      navigate(redirect);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo iniciar sesión');
    }
  };

  return (
    <>
      <CatalogBanner
        title="Acceso clientes"
        subtitle="Usa tu número de cuenta o correo y tu contraseña"
      />
      <div className="page-section">
        <div className="container-app max-w-md">
          <Panel title="Iniciar sesión">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </p>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-surface-600">
                  Cuenta o correo
                </label>
                <input
                  className="input-field"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  placeholder="Ej. C-1042 o cliente@empresa.com"
                  required
                  autoComplete="username"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-surface-600">
                  Contraseña
                </label>
                <input
                  type="password"
                  className="input-field"
                  value={clave}
                  onChange={(e) => setClave(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>
              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? 'Entrando…' : 'Entrar'}
              </button>
            </form>
            <p className="mt-4 text-center text-xs text-surface-500">
              <Link
                to={
                  usuario.includes('@')
                    ? `/recuperar?correo=${encodeURIComponent(usuario.trim())}`
                    : '/recuperar'
                }
                className="text-brand-700 hover:underline"
              >
                Olvidé mi contraseña o quiero crearla
              </Link>
            </p>
            <p className="mt-2 text-center text-xs">
              <Link to="/checkout" className="text-brand-700 hover:underline">
                Comprar sin cuenta
              </Link>
            </p>
          </Panel>
        </div>
      </div>
    </>
  );
}

export function CuentaPage() {
  const { cliente, pedidos, logout, refresh, token } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/ingresar?redirect=/cuenta', { replace: true });
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [token, navigate, refresh]);

  if (!token) return null;

  if (loading && !cliente) {
    return (
      <div className="container-app py-16">
        <div className="h-8 w-40 animate-pulse rounded bg-surface-200" />
      </div>
    );
  }

  if (!cliente) {
    navigate('/ingresar?redirect=/cuenta', { replace: true });
    return null;
  }

  return (
    <>
      <CatalogBanner
        title={cliente.nombre}
        subtitle={`Cuenta ${cliente.cuenta}`}
        breadcrumb={[{ label: 'Mi cuenta' }]}
      />
      <div className="page-section">
        <div className="container-app grid gap-6 lg:grid-cols-2">
          <Panel title="Datos en sistema">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4 border-b border-surface-100 py-2">
                <dt className="text-surface-500">Correo</dt>
                <dd>{cliente.email || '—'}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-surface-100 py-2">
                <dt className="text-surface-500">Teléfono</dt>
                <dd>{cliente.telefono || '—'}</dd>
              </div>
              <div className="flex justify-between gap-4 border-b border-surface-100 py-2">
                <dt className="text-surface-500">Dirección</dt>
                <dd className="text-right">
                  {[cliente.calle, cliente.numero, cliente.colonia, cliente.municipio]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </dd>
              </div>
              {cliente.tiene_facturacion && (
                <div className="flex justify-between gap-4 py-2">
                  <dt className="text-surface-500">RFC</dt>
                  <dd className="font-mono">{cliente.rfc_fiscal}</dd>
                </div>
              )}
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link to="/checkout" className="btn-primary">
                Ir a checkout
              </Link>
              <button type="button" className="btn-secondary" onClick={() => logout()}>
                Cerrar sesión
              </button>
            </div>
          </Panel>

          <Panel title="Mis pedidos" className="lg:col-span-2">
            {pedidos.length === 0 ? (
              <div className="py-6 text-center">
                <Package className="mx-auto h-10 w-10 text-surface-300" />
                <p className="mt-3 text-sm text-surface-500">Aún no tienes pedidos en la tienda.</p>
                <Link to="/catalogo" className="btn-primary mt-4 inline-block">
                  Ver catálogo
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {pedidos.map((p) => (
                  <PedidoCard key={p.folio_web} pedido={p} />
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}

function PedidoCard({ pedido }: { pedido: PedidoCliente }) {
  return (
    <Link
      to={`/cuenta/pedidos/${encodeURIComponent(pedido.folio_web)}`}
      className="block rounded-xl border border-surface-200 bg-white p-4 transition hover:border-brand-300 hover:shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-sm font-semibold text-surface-900">{pedido.folio_web}</p>
          <p className="mt-1 text-xs text-surface-500">{formatearFechaPedido(pedido.creado_en)}</p>
        </div>
        <p className="text-lg font-bold tabular-nums text-brand-700">{formatMXN(pedido.total)}</p>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${claseBadgePago(pedido.estatus)}`}>
          {etiquetaEstatusPago(pedido.estatus)}
        </span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${claseBadgeEnvio(pedido.estatus_envio)}`}>
          {etiquetaEstatusEnvio(pedido.estatus_envio)}
        </span>
        <span className="text-xs text-surface-500">
          {pedido.cantidad_articulos} artículo{pedido.cantidad_articulos !== 1 ? 's' : ''}
        </span>
      </div>
    </Link>
  );
}

export function PedidoDetallePage() {
  const { folio } = useParams<{ folio: string }>();
  const { token, refresh } = useAuthStore();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState<PedidoDetalle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      navigate('/ingresar?redirect=/cuenta', { replace: true });
      return;
    }
    if (!folio) return;

    setLoading(true);
    setError(null);
    refresh()
      .then(() => api.authPedido(folio))
      .then(setPedido)
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudo cargar el pedido'))
      .finally(() => setLoading(false));
  }, [token, folio, navigate, refresh]);

  if (!token) return null;

  if (loading) {
    return (
      <div className="container-app py-16">
        <div className="h-8 w-56 animate-pulse rounded bg-surface-200" />
      </div>
    );
  }

  if (error || !pedido) {
    return (
      <div className="container-app max-w-lg py-16 text-center">
        <p className="text-red-600">{error ?? 'Pedido no encontrado'}</p>
        <Link to="/cuenta" className="btn-primary mt-6 inline-block">
          Volver a mi cuenta
        </Link>
      </div>
    );
  }

  const direccion = [
    pedido.envio?.calle,
    pedido.envio?.numero,
    pedido.envio?.colonia,
    pedido.envio?.municipio,
    pedido.envio?.estado,
    pedido.envio?.codigo_postal,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <CatalogBanner
        title={pedido.folio_web}
        subtitle={formatearFechaPedido(pedido.creado_en)}
        breadcrumb={[
          { label: 'Mi cuenta', to: '/cuenta' },
          { label: pedido.folio_web },
        ]}
      />
      <div className="page-section">
        <div className="container-app grid max-w-4xl gap-6">
          <div className="flex flex-wrap gap-2">
            <span className={`rounded-full px-3 py-1 text-sm font-medium ${claseBadgePago(pedido.estatus)}`}>
              {etiquetaEstatusPago(pedido.estatus)}
            </span>
            <span className={`rounded-full px-3 py-1 text-sm font-medium ${claseBadgeEnvio(pedido.estatus_envio)}`}>
              {etiquetaEstatusEnvio(pedido.estatus_envio)}
            </span>
          </div>

          <Panel title="Artículos">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-200 text-left text-surface-500">
                  <th className="py-2">Producto</th>
                  <th className="py-2 text-center">Cant.</th>
                  <th className="py-2 text-right">Precio</th>
                  <th className="py-2 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {pedido.items.map((item, i) => (
                  <tr key={i} className="border-b border-surface-100">
                    <td className="py-2 pr-2">{item.descripcion}</td>
                    <td className="py-2 text-center tabular-nums">{item.cantidad}</td>
                    <td className="py-2 text-right tabular-nums">{formatMXN(item.precio_unitario)}</td>
                    <td className="py-2 text-right tabular-nums">{formatMXN(item.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 space-y-1 border-t border-surface-200 pt-4 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-500">Subtotal</span>
                <span>{formatMXN(pedido.subtotal)}</span>
              </div>
              {pedido.requiere_factura && pedido.iva > 0 && (
                <div className="flex justify-between">
                  <span className="text-surface-500">IVA</span>
                  <span>{formatMXN(pedido.iva)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold">
                <span>Total</span>
                <span className="text-brand-700">{formatMXN(pedido.total)}</span>
              </div>
            </div>
          </Panel>

          <div className="grid gap-6 sm:grid-cols-2">
            <Panel title="Envío">
              <p className="text-sm text-surface-700">{direccion || 'Sin dirección registrada'}</p>
            </Panel>
            <Panel title="Pago">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-surface-500">Método</dt>
                  <dd>{etiquetaMetodoPago(pedido)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-surface-500">Factura</dt>
                  <dd>{pedido.requiere_factura ? 'Sí' : 'No'}</dd>
                </div>
                {pedido.id_venta && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-surface-500">Venta ERP</dt>
                    <dd className="font-mono">#{pedido.id_venta}</dd>
                  </div>
                )}
              </dl>
            </Panel>
          </div>

          <Link to="/cuenta" className="btn-secondary w-fit">
            ← Volver a mi cuenta
          </Link>
        </div>
      </div>
    </>
  );
}
